/*
 * Iter 31.305 (D4) — Mureka Bulk Export panel.
 *
 * One-tap CSV download of the full Mureka library for this env
 * (preview OR production, whichever this admin session is talking
 * to). Manifest includes mureka_url share links + drive_file_id
 * for any audio already mirrored, so One can walk the export and
 * pull tracks via either channel.
 *
 * Deliberately minimal — this is a one-off deliverable per Two's
 * brief. Once One has both env exports the tile can be removed.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, getToken } from "@/lib/api";
import { Loader2, Download, AlertTriangle, Music4 } from "lucide-react";

export default function MurekaExportPanel() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/mureka-library/summary");
            setSummary(data);
            setErr(null);
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const download = () => {
        // Iter 31.305 hotfix (24 Jul 2026) — auth token lives under
        // `msd_token`, not `token`. Use the exported `getToken()` so
        // future key renames only touch api.js.
        const token = getToken();
        if (!token) {
            window.alert("Not signed in — token missing.");
            return;
        }
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/mureka-library/export`;
        (async () => {
            try {
                const resp = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!resp.ok) {
                    const detail = await resp.text().catch(() => "");
                    throw new Error(`HTTP ${resp.status}${detail ? ` — ${detail.slice(0, 200)}` : ""}`);
                }
                const blob = await resp.blob();
                const cd = resp.headers.get("content-disposition") || "";
                const m = cd.match(/filename="([^"]+)"/);
                const filename = m ? m[1] : "mureka_library.csv";
                const objUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = objUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(objUrl);
            } catch (e) {
                window.alert(`Export failed: ${e.message}`);
            }
        })();
    };

    return (
        <div
            className="paper-card border-2 border-ink/30 p-5 mb-6"
            data-testid="mureka-export-panel"
        >
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink flex items-center gap-2">
                    <Music4 className="w-4 h-4 text-stamp-red" />
                    Mureka Library — Bulk Export
                </h2>
                <button
                    type="button"
                    onClick={reload}
                    disabled={loading}
                    data-testid="mx-refresh"
                    className="font-heading uppercase tracking-[0.16em] text-[10px] text-ink-soft hover:text-stamp-red disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "refresh"}
                </button>
            </div>

            <div className="font-body text-[11px] text-ink-soft mb-3 leading-relaxed">
                Iter 31.305 — CSV manifest of every Mureka-catalogued track for this env, with share links + drive_file_id where mirrored. Run once on preview + once on prod to capture both slices.
            </div>

            {err ? (
                <div className="mb-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3 text-stamp-red flex items-start gap-1" data-testid="mx-err">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{err}</span>
                </div>
            ) : null}

            {summary ? (
                <div className="border border-ink/20 p-3 mb-3" data-testid="mx-summary">
                    <div className="font-body text-[11px] text-ink-soft">
                        Env <code className="font-mono text-stamp-red">{summary.env}</code>{" "}
                        holds{" "}
                        <span className="font-mono text-stamp-red font-bold" data-testid="mx-total">{summary.total}</span>{" "}
                        track(s){" "}
                        {summary.with_drive_file > 0 ? (
                            <span className="text-ink-faint">
                                (of which <span className="font-mono">{summary.with_drive_file}</span> are Drive-mirrored)
                            </span>
                        ) : null}
                    </div>
                    {summary.by_status?.length ? (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {summary.by_status.map((b) => (
                                <span
                                    key={b.status}
                                    className="inline-block px-2 py-0.5 border border-ink/25 font-body text-[10px] text-ink-soft"
                                    data-testid={`mx-status-${b.status}`}
                                >
                                    <span className="font-mono text-ink font-bold">{b.count}</span>{" "}
                                    · {b.status}
                                </span>
                            ))}
                        </div>
                    ) : null}
                    {/* Iter 31.305a — by-user breakdown for cross-uid diagnosis. */}
                    {summary.by_user?.length ? (
                        <div className="mt-2 font-body text-[10px] text-ink-faint" data-testid="mx-by-user">
                            <div className="mb-1">
                                signed in as{" "}
                                <code className="font-mono">{summary.signed_in_as || "(none)"}</code>{" "}
                                · rows by user:
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {summary.by_user.map((b) => (
                                    <span
                                        key={b.user_id}
                                        className={`inline-block px-2 py-0.5 border font-body text-[10px] ${b.user_id === summary.signed_in_as ? "border-stamp-red/60 text-stamp-red" : "border-ink/20 text-ink-faint"}`}
                                        data-testid={`mx-user-${b.user_id}`}
                                    >
                                        <span className="font-mono font-bold">{b.count}</span>
                                        {" · "}
                                        <code className="font-mono">{b.user_id.slice(0, 12)}{b.user_id.length > 12 ? "…" : ""}</code>
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}

            <button
                type="button"
                onClick={download}
                data-testid="mx-download"
                className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 inline-flex items-center gap-1.5"
            >
                <Download className="w-3 h-3" />
                Download CSV
            </button>
            {summary && summary.total === 0 ? (
                <div className="mt-2 font-body text-[10px] text-ink-faint italic" data-testid="mx-zero-note">
                    Library is empty for this env — export will produce a header-only CSV.
                    Import the Mureka TSV first (see MurekaLibraryTile above) if this is unexpected.
                </div>
            ) : null}
        </div>
    );
}
